import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal, computed } from '@angular/core';
import { getFileTypeIconClass } from '@iupics-components/standard/preview-doc/preview-doc.component';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { DialogFocusDirective } from './dialog-focus.directive';
import { DocumentRecordPickerComponent } from './document-record-picker.component';
import { formatSize, tagName } from './documents-query';
import { DocDuplicate, DocTag, DocTarget, DocUploadOptions } from './documents.models';
import { DocumentsService } from './documents.service';
import { viewRefresher } from './view-refresh';

/** files hashed for the duplicate check up to this size (the browser reads them whole into memory) */
const HASH_MAX = 50 * 1024 * 1024;

interface UploadRow {
  file: File;
  include: boolean;
  tooBig: boolean;
  hashing: boolean;
  sha256: string | null;
  duplicates: DocDuplicate[];
}

/**
 * Before an upload: the files (duplicates of stored documents unticked, with the existing names), the record to
 * link them to, the user tags and the visibility.
 */
@Component({
  selector: 'iu-document-upload-dialog',
  standalone: true,
  imports: [TranslateModule, DocumentRecordPickerComponent, DialogFocusDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ud-backdrop" (click)="cancelled.emit()"></div>
    <section class="ud" role="dialog" aria-modal="true" iuDialogFocus [attr.aria-label]="'documents.upload.dialogTitle' | translate" (keydown.escape)="cancelled.emit()">
      <header class="ud-head">
        <h2>{{ 'documents.upload.dialogTitle' | translate }}</h2>
        <button type="button" class="ud-icon" (click)="cancelled.emit()" [attr.aria-label]="'documents.actions.close' | translate"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
      </header>
      <div class="ud-body">
        <ul class="ud-files">
          @for (r of rows(); track $index) {
            <li [class.ud-file--off]="!r.include">
              <input type="checkbox" [checked]="r.include" [disabled]="r.tooBig" (change)="toggle($index, $any($event.target).checked)" [attr.aria-label]="r.file.name" />
              <i [class]="icon(r.file.name)" aria-hidden="true"></i>
              <span class="ud-file-main">
                <span class="ud-file-name" [title]="r.file.name">{{ r.file.name }}</span>
                <span class="ud-muted">{{ size(r.file.size) }}
                  @if (r.hashing) { - <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i> {{ 'documents.upload.checking' | translate }} }
                </span>
                @if (r.tooBig) {
                  <span class="ud-warn">{{ 'documents.upload.tooBig' | translate: { max: size(maxUploadBytes()) } }}</span>
                }
                @for (d of r.duplicates; track d.docId) {
                  <span class="ud-warn"><i class="fa-solid fa-clone" aria-hidden="true"></i>
                    {{ (d.trashed ? 'documents.upload.duplicateBin' : 'documents.upload.duplicate') | translate: { name: d.fileName } }}</span>
                }
              </span>
            </li>
          }
        </ul>

        <h3>{{ 'documents.upload.linkTo' | translate }}</h3>
        <iu-document-record-picker [value]="target()" (valueChange)="target.set($event)" />

        <h3>{{ 'documents.detail.tags' | translate }}</h3>
        <div class="ud-tags">
          <label class="ud-tag ud-tag--wide">
            <span>{{ 'documents.tags.description' | translate }}</span>
            <input class="p-inputtext ud-input" maxlength="1000" [value]="tags()['DESCRIPTION'] ?? ''" (input)="setTag('DESCRIPTION', $any($event.target).value)" />
          </label>
          <label class="ud-tag">
            <span>{{ 'documents.tags.keywords' | translate }}</span>
            <input class="p-inputtext ud-input" maxlength="1000" [placeholder]="'documents.tags.keywordsHelp' | translate" [value]="tags()['KEYWORDS'] ?? ''" (input)="setTag('KEYWORDS', $any($event.target).value)" />
          </label>
        </div>
        <!-- other tags: any name (the server adds the internal prefix) -->
        @for (c of custom(); track c.id) {
          <div class="ud-custom">
            <input class="p-inputtext ud-input" list="ud-tag-names" maxlength="60" [placeholder]="'documents.tagEditor.name' | translate" [value]="c.name" (input)="setCustom(c.id, 'name', $any($event.target).value)" />
            <input class="p-inputtext ud-input" maxlength="1000" [placeholder]="'documents.tagEditor.value' | translate" [value]="c.value" (input)="setCustom(c.id, 'value', $any($event.target).value)" />
            <button type="button" class="ud-icon" (click)="removeCustom(c.id)" [attr.aria-label]="'documents.tagEditor.remove' | translate"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
          </div>
        }
        <button type="button" class="ud-link" (click)="addCustom()"><i class="fa-solid fa-plus" aria-hidden="true"></i> {{ 'documents.tagEditor.add' | translate }}</button>
        <datalist id="ud-tag-names">
          @for (t of tagDefs(); track t.key) {
            @if (t.editable && t.key !== 'DESCRIPTION' && t.key !== 'KEYWORDS') {
              <option [value]="t.key">{{ t.label }}</option>
            }
          }
        </datalist>

        <label class="ud-check" [title]="'documents.upload.allRolesHelp' | translate">
          <input type="checkbox" [checked]="allRoles()" (change)="allRoles.set($any($event.target).checked)" />
          {{ 'documents.upload.allRoles' | translate }}
        </label>
      </div>
      <footer class="ud-foot">
        <span class="ud-muted">{{ 'documents.upload.selected' | translate: { count: included().length, size: size(includedSize()) } }}</span>
        <span class="ud-spacer"></span>
        <button type="button" class="p-button p-button-secondary ud-btn" (click)="cancelled.emit()">{{ 'documents.actions.cancel' | translate }}</button>
        <button type="button" class="p-button p-button-primary ud-btn" [disabled]="!included().length || checking()" (click)="confirm()">
          <i class="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i>{{ 'documents.actions.upload' | translate }}
        </button>
      </footer>
    </section>
  `,
  styles: `
    :host { position: absolute; inset: 0; z-index: 20; display: flex; align-items: center; justify-content: center; }
    .ud-backdrop { position: absolute; inset: 0; background: #0006; }
    .ud { position: relative; display: flex; flex-direction: column; width: min(640px, calc(100% - 32px)); max-height: calc(100% - 32px);
      border: 1px solid var(--fieldset-bd-color); border-radius: var(--global-border-radius); background: var(--fieldset-bg-color);
      color: var(--global-text-color-1); box-shadow: var(--global-shadow-box); }
    .ud-head, .ud-foot { display: flex; align-items: center; gap: 8px; padding: 10px 14px; }
    .ud-head { border-bottom: 1px solid var(--docs-bd-color); }
    .ud-head h2 { flex: 1 1 auto; margin: 0; font-size: 15px; }
    .ud-foot { border-top: 1px solid var(--docs-bd-color); flex-wrap: wrap; }
    @media (max-width: 480px) { .ud-custom { flex-wrap: wrap; } .ud-custom .ud-input { flex: 1 1 100%; } }
    @media (pointer: coarse) { .ud-input, .ud-icon { min-height: 36px; } }
    .ud-body { flex: 1 1 auto; min-height: 0; overflow: auto; padding: 10px 14px; }
    .ud-body h3 { margin: 14px 0 6px; font-size: 12px; font-weight: normal; text-transform: uppercase; letter-spacing: 0.06em; color: var(--section-label-ft-color); }
    .ud-files { margin: 0; padding: 0; list-style: none; max-height: 220px; overflow: auto; }
    .ud-files li { display: flex; align-items: flex-start; gap: 8px; padding: 5px 0; border-bottom: 1px solid var(--docs-bd-color); }
    .ud-files li > i { font-size: 16px; color: var(--docs-ft-color-muted); margin-top: 2px; }
    .ud-file--off .ud-file-name { text-decoration: line-through; color: var(--docs-ft-color-muted); }
    .ud-file-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1 1 auto; }
    .ud-file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ud-muted { color: var(--docs-ft-color-muted); font-size: 11px; }
    .ud-warn { display: flex; align-items: center; gap: 4px; color: var(--docs-color-warning); font-size: 11px; }
    .ud-tags { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 8px 10px; }
    .ud-tag { display: flex; flex-direction: column; gap: 3px; font-size: 11px; color: var(--docs-ft-color-muted); }
    .ud-tag--wide { grid-column: 1 / -1; }
    .ud-input { height: 28px; padding: 0 8px; font: inherit; font-size: var(--font-size); color: var(--global-text-color-1); border: 1px solid var(--input-bd-color); border-radius: var(--docs-br); background: var(--input-bg-color); }
    .ud-custom { display: flex; gap: 6px; margin-top: 6px; }
    .ud-custom .ud-input { flex: 1 1 0; min-width: 0; }
    .ud-link { margin-top: 6px; padding: 2px 0; border: none; background: transparent; color: var(--docs-ft-color-link); font: inherit; cursor: pointer; }
    .ud-check { display: inline-flex; align-items: center; gap: 6px; margin-top: 12px; cursor: pointer; }
    .ud-spacer { flex: 1 1 auto; }
    .ud-icon { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: none; background: transparent; color: inherit; cursor: pointer; border-radius: var(--docs-br); }
    .ud-icon:hover { background: var(--docs-bg-color-hover); }
    .p-button.ud-btn { display: inline-flex; align-items: center; gap: 6px; height: 28px; margin: 0; padding: 0 12px; font-size: var(--font-size); }
  `,
})
export class DocumentUploadDialogComponent implements OnInit {
  readonly files = input.required<File[]>();
  readonly initialTarget = input<DocTarget | null>(null);
  readonly tagDefs = input<DocTag[]>([]);
  readonly maxUploadBytes = input(0);
  readonly initialAllRoles = input(false);

  readonly confirmed = output<{ files: File[]; options: DocUploadOptions }>();
  readonly cancelled = output<void>();

  readonly #docs = inject(DocumentsService);
  readonly #refresh = viewRefresher();

  readonly rows = signal<UploadRow[]>([]);
  readonly target = signal<DocTarget | null>(null);
  readonly tags = signal<Record<string, string>>({});
  readonly allRoles = signal(false);
  readonly included = computed(() => this.rows().filter((r) => r.include && !r.tooBig));
  readonly includedSize = computed(() => this.included().reduce((n, r) => n + r.file.size, 0));
  /** duplicate check running: upload waits for it */
  readonly checking = signal(false);

  icon = (name: string) => getFileTypeIconClass(name);
  size = (n: number) => formatSize(n);

  ngOnInit(): void {
    const max = this.maxUploadBytes();
    this.rows.set(
      this.files().map((file) => ({
        file,
        include: !(max > 0 && file.size > max),
        tooBig: max > 0 && file.size > max,
        hashing: false,
        sha256: null,
        duplicates: [],
      }))
    );
    this.target.set(this.initialTarget());
    this.allRoles.set(this.initialAllRoles());
    void this.#checkDuplicates();
  }

  toggle(index: number, include: boolean): void {
    this.rows.update((rows) => rows.map((r, i) => (i === index ? { ...r, include } : r)));
  }

  setTag(key: string, value: string): void {
    this.tags.update((t) => ({ ...t, [key]: value }));
  }

  readonly custom = signal<{ id: number; name: string; value: string }[]>([]);
  #customSeq = 0;

  addCustom(): void {
    this.custom.update((c) => [...c, { id: ++this.#customSeq, name: '', value: '' }]);
  }

  setCustom(id: number, field: 'name' | 'value', value: string): void {
    this.custom.update((c) => c.map((x) => (x.id === id ? { ...x, [field]: value } : x)));
  }

  removeCustom(id: number): void {
    this.custom.update((c) => c.filter((x) => x.id !== id));
  }

  confirm(): void {
    const tags: Record<string, string> = {};
    for (const [k, v] of Object.entries(this.tags())) if (v.trim()) tags[k] = v.trim();
    // names typed without prefix; invalid names are left out
    for (const c of this.custom()) {
      const name = tagName(c.name);
      if (/^[A-Z0-9_]{1,60}$/.test(name) && c.value.trim()) tags[name] = c.value.trim();
    }
    this.confirmed.emit({
      files: this.included().map((r) => r.file),
      options: { target: this.target(), allRoles: this.allRoles(), tags },
    });
  }

  /** SHA-256 of each file in the browser, then one question to the server; duplicates are unticked */
  async #checkDuplicates(): Promise<void> {
    if (!globalThis.crypto?.subtle) return;
    this.checking.set(true);
    try {
      await this.#hashAndCheck();
    } finally {
      this.checking.set(false);
      this.#refresh();
    }
  }

  async #hashAndCheck(): Promise<void> {
    const rows = this.rows();
    for (let i = 0; i < rows.length; i++) {
      const r = this.rows()[i];
      if (r.tooBig || r.file.size > HASH_MAX || r.file.size === 0) continue;
      this.#patch(i, { hashing: true });
      try {
        const digest = await crypto.subtle.digest('SHA-256', await r.file.arrayBuffer());
        const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
        this.#patch(i, { hashing: false, sha256: hex });
      } catch {
        this.#patch(i, { hashing: false });
      }
    }
    const hashes = this.rows()
      .map((r) => r.sha256)
      .filter((h): h is string => !!h);
    if (!hashes.length) return;
    try {
      const found = await firstValueFrom(this.#docs.duplicates(hashes));
      this.rows.update((list) =>
        list.map((r) => {
          const dups = found.filter((d) => d.sha256 === r.sha256);
          return dups.length ? { ...r, duplicates: dups, include: false } : r;
        })
      );
      this.#refresh();
    } catch {
      // no duplicate check: the upload still works
    }
  }

  #patch(index: number, change: Partial<UploadRow>): void {
    this.rows.update((list) => list.map((r, i) => (i === index ? { ...r, ...change } : r)));
    this.#refresh();
  }
}
